import { useState, useCallback, useEffect, useContext } from 'react';
import { ArrowBack, HomeWork, AssignmentLate, Nightlife, Villa } from '@mui/icons-material';
import { useParams, useNavigate, Routes, Route, Navigate, useMatch } from 'react-router-dom';
import Sidenav from '../../components/Sidebar/Sidenav';
import NavItem from '../../components/Sidebar/NavItem';
import { CondominiumPages } from './pages/types';
import BasicInfo from './pages/BasicInfo';
import Sections from './pages/Sections';
import CommonAreas from './pages/CommonAreas';
import Units from './pages/Units';
import CondominiumContext from '../../contexts/CondominiumContext';
import { useCondominiums, useFetch } from '../../hooks';

const CondominiumRouter = (): JSX.Element => {
  const { pathnameBase } = useMatch({ path: '/condominiums/:id/*' })!;

  return (
    <Routes>
      <Route path="/" element={<Navigate to={`${pathnameBase}/basic-data`} replace />} />

      <Route path="/basic-data" element={<BasicInfo />} />

      <Route path="/sections" element={<Sections />} />

      <Route path="/units" element={<Units />} />

      <Route path="/common-areas" element={<CommonAreas />} />
    </Routes>
  );
};

export const Condominium = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const history = useNavigate();
  const match = useMatch({ path: '/condominiums/:id/*' });
  const { setCondominium, setLoading, handleUnselectCondominium } = useContext(CondominiumContext);
  const [activePage, setPage] = useState<CondominiumPages>();
  const condominiumsRepository = useCondominiums();
  const { data, refetch, loading } = useFetch(
    (selectedId?: string) => condominiumsRepository.fetchById((selectedId ?? id)!),
    { ignoreFirstCall: true }
  );

  useEffect(() => {
    if (id !== 'new') {
      refetch(id);
    }
  }, [id, refetch]);

  useEffect(() => setLoading(loading), [loading, setLoading]);

  useEffect(() => {
    setCondominium(data ?? null);
  }, [data, setCondominium]);

  useEffect(() => {
    if (match) {
      const { pathnameBase, pathname: _path } = match;

      const initialRoute = _path.replace(pathnameBase, '').replace('/', '');

      setPage(initialRoute as CondominiumPages);
    }
  }, [match]);

  const handlePageChange = useCallback(
    (page: CondominiumPages) => {
      setPage(page);
      history(page);
    },
    [history]
  );

  const getActivePage = useCallback((page: CondominiumPages) => page === activePage, [activePage]);

  return (
    <>
      <Sidenav>
        <>
          <NavItem title="voltar" icon={<ArrowBack />} onClick={() => handleUnselectCondominium()} />
          <NavItem
            title="dados básicos"
            icon={<AssignmentLate />}
            active={getActivePage(CondominiumPages.BasicData)}
            onClick={() => handlePageChange(CondominiumPages.BasicData)}
          />
          {id !== 'new' && (
            <>
              <NavItem
                title="blocos"
                icon={<Villa />}
                active={getActivePage(CondominiumPages.Sections)}
                onClick={() => handlePageChange(CondominiumPages.Sections)}
              />
              <NavItem
                title="unidades"
                icon={<HomeWork />}
                active={getActivePage(CondominiumPages.Units)}
                onClick={() => handlePageChange(CondominiumPages.Units)}
              />
              <NavItem
                title="áreas comuns"
                icon={<Nightlife />}
                active={getActivePage(CondominiumPages.CommonAreas)}
                onClick={() => handlePageChange(CondominiumPages.CommonAreas)}
              />
            </>
          )}
        </>
      </Sidenav>
      {!loading && <CondominiumRouter />}
    </>
  );
};

export default Condominium;
